import React, { useState, useEffect } from 'react';
import {
  FileText,
  Download,
  Users,
  BookOpen,
  DollarSign,
  TrendingUp,
  Eye,
  Mail,
} from 'lucide-react';
import { analyticsApi, studentsApi, coursesApi, uploadApi } from '@/services/api';
import { toast } from 'sonner';

interface Report {
  id: string;
  name: string;
  description: string;
  type: 'students' | 'courses' | 'revenue' | 'activity';
  lastGenerated: string;
  status: 'ready' | 'generating' | 'scheduled';
}

interface GeneratedReport {
  id: string;
  name: string;
  generatedAt: string;
  size: string;
  format: 'PDF' | 'Excel' | 'CSV';
  downloads: number;
}

// Función para generar templates dinámicos basados en datos reales
const getReportTemplates = (reportData: any): Report[] => [
  {
    id: '1',
    name: 'Reporte de Estudiantes',
    description: `${reportData.students?.total || 0} estudiantes registrados con datos completos`,
    type: 'students',
    lastGenerated: new Date().toISOString(),
    status: 'ready',
  },
  {
    id: '2',
    name: 'Análisis de Cursos',
    description: `Datos detallados de todos los cursos con métricas de rendimiento`,
    type: 'courses',
    lastGenerated: new Date().toISOString(),
    status: 'ready',
  },
  {
    id: '3',
    name: 'Reporte de Ingresos',
    description: `Análisis financiero completo con desglose de pagos y métricas`,
    type: 'revenue',
    lastGenerated: new Date().toISOString(),
    status: 'ready',
  },
  {
    id: '4',
    name: 'Galería de Archivos',
    description: 'Listado completo de videos, imágenes y documentos subidos',
    type: 'activity',
    lastGenerated: new Date().toISOString(),
    status: 'ready',
  },
];

// Los reportes generados se manejarán dinámicamente

// Componente para la galería de archivos
const FilesGallery: React.FC = () => {
  const [files, setFiles] = useState<any[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(true);

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      setIsLoadingFiles(true);
      const response = await uploadApi.getContent();
      if (response.data.success) {
        setFiles(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
      toast.error('Error al cargar archivos');
    } finally {
      setIsLoadingFiles(false);
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    if (!confirm('¿Estás seguro de eliminar este archivo?')) return;
    
    try {
      await uploadApi.deleteFile(fileId);
      toast.success('Archivo eliminado exitosamente');
      await fetchFiles();
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Error al eliminar archivo');
    }
  };

  const getFileIcon = (mimetype: string) => {
    if (mimetype?.startsWith('video/')) return '🎥';
    if (mimetype?.startsWith('image/')) return '🖼️';
    return '📄';
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">
        Galería de Archivos
      </h2>

      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Videos, Imágenes y Documentos
            </h3>
            <span className="text-sm text-gray-500">
              {files.length} archivos totales
            </span>
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {isLoadingFiles ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-gray-500">Cargando archivos...</p>
            </div>
          ) : files.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-gray-500">No hay archivos subidos</p>
            </div>
          ) : (
            files.map((file) => (
              <div key={file.id} className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="text-2xl">
                      {getFileIcon(file.mimetype)}
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">
                        {file.originalName || file.filename}
                      </h4>
                      <p className="text-xs text-gray-500">
                        {file.course?.title || 'Sin curso'} • 
                        {file.size ? ` ${(file.size / (1024 * 1024)).toFixed(2)} MB` : ' Tamaño desconocido'} • 
                        {file.createdAt ? new Date(file.createdAt).toLocaleDateString() : 'Fecha desconocida'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {file.url && (
                      <a 
                        href={file.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="p-2 text-blue-600 hover:text-blue-800"
                        title="Ver archivo"
                      >
                        <Eye className="w-4 h-4" />
                      </a>
                    )}
                    {file.url && (
                      <a 
                        href={file.url} 
                        download={file.originalName || file.filename}
                        className="p-2 text-green-600 hover:text-green-800"
                        title="Descargar archivo"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                    )}
                    <button 
                      onClick={() => handleDeleteFile(file.id)}
                      className="p-2 text-red-600 hover:text-red-800"
                      title="Eliminar archivo"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export const ReportsPage: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedFormat, setSelectedFormat] = useState('pdf');
  const [selectedReportType, setSelectedReportType] = useState('general');
  const [isGenerating, setIsGenerating] = useState<string | null>(null);
  const [reportData, setReportData] = useState<any>({});
  const [isLoading, setIsLoading] = useState(true);
  const [generatedReports, setGeneratedReports] = useState<GeneratedReport[]>([]);

  useEffect(() => {
    fetchReportData();
  }, []);

  const fetchReportData = async () => {
    try {
      setIsLoading(true);
      
      const [metricsResponse, studentsResponse, coursesResponse] = await Promise.all([
        analyticsApi.getMetrics(),
        studentsApi.getStats(),
        coursesApi.getAll({ limit: 100 })
      ]);

      setReportData({
        metrics: (metricsResponse.data as any)?.success ? (metricsResponse.data as any).data : {},
        students: (studentsResponse.data as any)?.success ? (studentsResponse.data as any).data : {},
        courses: (coursesResponse.data as any)?.success ? (coursesResponse.data as any).data : {}
      });
    } catch (error) {
      console.error('Error fetching report data:', error);
      toast.error('Error al cargar datos de reportes');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getReportIcon = (type: string) => {
    switch (type) {
      case 'students': return <Users className="w-5 h-5 text-blue-500" />;
      case 'courses': return <BookOpen className="w-5 h-5 text-green-500" />;
      case 'revenue': return <DollarSign className="w-5 h-5 text-purple-500" />;
      case 'activity': return <TrendingUp className="w-5 h-5 text-orange-500" />;
      default: return <FileText className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ready':
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
            Listo
          </span>
        );
      case 'generating':
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
            Generando...
          </span>
        );
      case 'scheduled':
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
            Programado
          </span>
        );
      default:
        return null;
    }
  };


  const handleGenerateReport = async (reportId: string) => {
    if (isLoading) {
      toast.error('Cargando datos, espera un momento...');
      return;
    }

    setIsGenerating(reportId);
    
    try {
      // Generar reporte usando el nuevo endpoint del backend
      const endDate = new Date();
      const startDate = new Date();
      
      // Configurar fecha de inicio según período seleccionado
      switch (selectedPeriod) {
        case 'week':
          startDate.setDate(endDate.getDate() - 7);
          break;
        case 'month':
          startDate.setMonth(endDate.getMonth() - 1);
          break;
        case 'quarter':
          startDate.setMonth(endDate.getMonth() - 3);
          break;
        case 'year':
          startDate.setFullYear(endDate.getFullYear() - 1);
          break;
        default:
          startDate.setMonth(endDate.getMonth() - 1); // Por defecto último mes
      }
      
      let format = selectedFormat;
      let csvType = 'students';
      let reportType = selectedReportType;

      // Mapear reportId a csvType y reportType
      switch (reportId) {
        case '1':
          csvType = 'students';
          reportType = 'students';
          break;
        case '2':
          csvType = 'courses';
          reportType = 'courses';
          break;
        case '3':
          csvType = 'revenue';
          reportType = 'revenue';
          break;
        case '4':
          csvType = 'enrollments';
          reportType = 'enrollments';
          break;
        default:
          reportType = 'general';
      }

      // Llamar al nuevo endpoint de generación de reportes
      const response = await analyticsApi.generateReport({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        format: format as 'pdf' | 'excel' | 'csv',
        csvType: csvType as 'students' | 'courses' | 'revenue' | 'enrollments',
        type: reportType as 'general' | 'students' | 'courses' | 'revenue' | 'enrollments'
      });
      
      if ((response.data as any)?.success) {
        const { fileName, downloadUrl, format: reportFormat } = (response.data as any).data;
        
        // Descargar el archivo generado - usar URL absoluta
        const baseUrl = import.meta.env.VITE_API_URL.replace('/api/v1', '');
        const downloadResponse = await fetch(`${baseUrl}${downloadUrl}`);
        const blob = await downloadResponse.blob();
        
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        // Agregar reporte a la lista de generados
        const newReport: GeneratedReport = {
          id: Date.now().toString(),
          name: fileName,
          generatedAt: new Date().toISOString(),
          size: `${(blob.size / 1024).toFixed(1)} KB`,
          format: reportFormat.toUpperCase() as 'PDF' | 'Excel' | 'CSV',
          downloads: 1
        };
        setGeneratedReports(prev => [newReport, ...prev]);
        
        toast.success('Reporte generado y descargado exitosamente');
      } else {
        throw new Error('Error en la respuesta del servidor');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Error al generar el reporte');
    } finally {
      setIsGenerating(null);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Reportes</h1>
          <p className="text-gray-600">
            Genera y descarga reportes detallados de tu plataforma
          </p>
        </div>

        <button className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors">
          <FileText className="w-4 h-4" />
          <span>Crear Reporte Personalizado</span>
        </button>
      </div>

      {/* Report Configuration */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Configuración de Reportes
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Período
            </label>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="week">Esta semana</option>
              <option value="month">Este mes</option>
              <option value="quarter">Este trimestre</option>
              <option value="year">Este año</option>
              <option value="custom">Personalizado</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Formato
            </label>
            <select
              value={selectedFormat}
              onChange={(e) => setSelectedFormat(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="pdf">PDF</option>
              <option value="excel">Excel</option>
              <option value="csv">CSV</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Reporte
            </label>
            <select
              value={selectedReportType}
              onChange={(e) => setSelectedReportType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="general">General</option>
              <option value="students">Estudiantes</option>
              <option value="courses">Cursos</option>
              <option value="revenue">Ingresos</option>
              <option value="enrollments">Inscripciones</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Envío Automático
            </label>
            <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500">
              <option value="none">Sin programar</option>
              <option value="daily">Diario</option>
              <option value="weekly">Semanal</option>
              <option value="monthly">Mensual</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Available Reports */}
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Reportes Disponibles
          </h2>

          <div className="space-y-4">
            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-500">Cargando datos de reportes...</p>
              </div>
            ) : (
              getReportTemplates(reportData).map((report) => (
              <div key={report.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start space-x-3">
                    {getReportIcon(report.type)}
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-1">
                        {report.name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {report.description}
                      </p>
                    </div>
                  </div>
                  
                  {getStatusBadge(report.status)}
                </div>

                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    Último: {formatDate(report.lastGenerated)}
                  </p>
                  
                  <div className="flex space-x-2">
                    {report.status === 'ready' && (
                      <>
                        <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                          <Eye className="w-4 h-4" />
                        </button>
                        <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                          <Mail className="w-4 h-4" />
                        </button>
                        <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                          <Download className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    
                    <button
                      onClick={() => handleGenerateReport(report.id)}
                      disabled={isGenerating === report.id || report.status === 'generating'}
                      className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                      {isGenerating === report.id ? 'Generando...' : 'Generar'}
                    </button>
                  </div>
                </div>
              </div>
              ))
            )}
          </div>
        </div>

        {/* Generated Reports */}
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Reportes Generados
          </h2>

          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  Archivos Recientes
                </h3>
                <button className="text-sm text-primary-600 hover:text-primary-700">
                  Ver todos
                </button>
              </div>
            </div>

            <div className="divide-y divide-gray-200">
              {generatedReports.map((report) => (
                <div key={report.id} className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <FileText className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">
                          {report.name}
                        </h4>
                        <p className="text-xs text-gray-500">
                          {formatDate(report.generatedAt)} • {report.size}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-1 text-xs text-gray-500">
                        <Download className="w-3 h-3" />
                        <span>{report.downloads}</span>
                      </div>
                      
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded ${
                        report.format === 'PDF' ? 'bg-red-100 text-red-800' :
                        report.format === 'Excel' ? 'bg-green-100 text-green-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {report.format}
                      </span>

                      <div className="flex space-x-1">
                        <button className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
                          <Eye className="w-4 h-4" />
                        </button>
                        <button className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
                          <Download className="w-4 h-4" />
                        </button>
                        <button className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
                          <Mail className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Estadísticas Rápidas
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">24</p>
                <p className="text-sm text-blue-600">Reportes Generados</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">156</p>
                <p className="text-sm text-green-600">Descargas Totales</p>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Reporte más descargado:</span>
                <span className="font-medium text-gray-900">Ingresos Q4</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-2">
                <span className="text-gray-600">Último generado:</span>
                <span className="font-medium text-gray-900">Hace 2 horas</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Files Gallery Section */}
      <div className="mt-8">
        <FilesGallery />
      </div>
    </div>
  );
};